<template>
  <div id="factors-and-decisions">
	<p class="factors-and-decisions-description">Below, you can manage what configuration options are available to our customers as well as adjust the
		meta-data that our system uses to build the public interface. Changes here immediately impact the available pool
		for profile modification but do NOT replace or modify existing profile data.</p>
	<p class="factors-and-decisions-description">Note: To evaluate a new property, you’ll need to make an official development request.</p>

	<template v-if="initialDecisionConfigList.length !== 0 && availableFactorList.length !== 0">
		<div class="initial-decisions">
			<h3>Initial Decision Pool</h3>
			<v-data-table
				:headers="initialDecisionHeaders"
				:items="initialDecisionConfigList"
				:items-per-page="10"
				:hide-default-footer="initialDecisionConfigList.length <= 10"
				mobile-breakpoint="500"
				item-key="initialDecisionConfigId"
			>
			<template v-slot:item.label="{ item }">
				<div class="initial-decision-label">
					<template v-if="item.isActive === true">
						<v-icon class="active-profile" title="Active">mdi-account-group</v-icon>
					</template>
					<template v-else>
						<v-icon class="inactive-profile" title="Inactive">mdi-eye-off-outline</v-icon>
					</template>
					<div class="item-label">
						{{ item.label }}
					</div>
				</div>
			</template>

			<template v-slot:item.actions="{ item }">
				<v-btn text class="view-button" @click="viewInitialDecision(item)">
					View Decision
					<v-icon>mdi-chevron-right</v-icon>
				</v-btn>
				<v-menu
					v-model="item.more"
					:close-on-content-click="true"
					offset-y
					left
					transition="slide-y-transition"
					class="more-options"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-btn
							text
							v-bind="attrs"
							v-on="on"
							aria-label="menu"
							title="menu"
							class="more-options-icon"
						>
							<v-icon>mdi-dots-vertical</v-icon>
						</v-btn>
					</template>
					<div class="more-options-activator">
						<v-btn
							class="more-options-activate"
							@click="onDisableEnableInitialDecision(item)"
						>
							<template v-if="item.isActive">
								Disable
							</template>
							<template v-else>
								Enable
							</template>
						</v-btn>
					</div>
				</v-menu>
			</template>

			</v-data-table>
		</div>
		<div class="evaluation-factors">
			<h3>Evaluation Factor Pool</h3>
			<v-data-table
				:headers="evaluationFactorHeaders"
				:items="availableFactorList"
				:items-per-page="10"
				:hide-default-footer="availableFactorList.length <= 10"
				mobile-breakpoint="500"
				item-key="factorId"
			>

			<template v-slot:item.label="{ item }">
				<div class="evaluation-factors-label">
					<template v-if="item.isActive === true">
						<v-icon class="active-profile" title="Active">mdi-account-group</v-icon>
					</template>
					<template v-else>
						<v-icon class="inactive-profile" title="Inactive">mdi-eye-off-outline</v-icon>
					</template>
					<div class="item-label">
						{{ item.label }}
					</div>
				</div>
			</template>

			<template v-slot:item.treeMode="{ item }">
				<template v-if="(item.treeMode === 'Root') || (item.treeMode === 'Parent')">
					<v-icon class="evaluation-factors-type-icon">mdi-folder-outline</v-icon>Group</template>
				<template v-else>Factor</template>
			</template>

			<template v-slot:item.actions="{ item }">
				<v-btn text class="view-button" @click="viewFactorDecisions(item)">
					View Factor
					<v-icon>mdi-chevron-right</v-icon>
				</v-btn>
				<v-menu
					v-model="item.more"
					:close-on-content-click="true"
					offset-y
					left
					transition="slide-y-transition"
					class="more-options"
				>
					<template v-slot:activator="{ on, attrs }">
						<v-btn
							text
							v-bind="attrs"
							v-on="on"
							aria-label="menu"
							title="menu"
							class="more-options-icon"
						>
							<v-icon>mdi-dots-vertical</v-icon>
						</v-btn>
					</template>
					<div class="more-options-activator">
						<v-btn
							class="more-options-activate"
							@click="onDisableEnableFactor(item)"
						>
							<template v-if="item.isActive">
								Disable
							</template>
							<template v-else>
								Enable
							</template>
						</v-btn>
					</div>
				</v-menu>
			</template>
			
			</v-data-table>
		</div>
	</template>
	<v-skeleton-loader v-else type="table"></v-skeleton-loader>
  </div>
</template>

<script>
	/**
	 * @fileoverview factors-and-decisions.vue displays the initial decision pool and the evaluation factor pool.
	 * It relies on vuex to map the state variables from the store files.
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */
	import { mapState } from "vuex";

	export default {
		data() {
			return {
				/**
				 * Initial decision pool headers
				 */
				initialDecisionHeaders: [
					{
						text: "Name",
						align: "start",
						value: "label",
					},
					{
						text: "",
						align: "end",
						value: "actions",
						sortable: false,
						class: "actions"
					},
				],
				/**
				 * Evaluation factor pool headers
				 */
				evaluationFactorHeaders: [
					{
						text: "Name",
						align: "start",
						value: "label",
					},
					{
						text: "Type",
						align: "start",
						value: "treeMode",
						class: "evaluation-factor-type"
					},
					{
						text: "",
						align: "end",
						value: "actions",
						sortable: false,
						class: "actions"
					},
				]
			}
		},
		computed: {
			...mapState({
				initialDecisionConfigList: (state) => state.config.initialDecisionConfigList,
				availableFactorList: (state) => state.config.availableFactorList,
				activeTenantId: (state) => state.account.activeTenantId,
			})
		},
		methods: {
			/**
			 * Emits the selected decision to configuration.vue to display the initial decision details
			 * @param {Object} item - initial decision object
			 */
			viewInitialDecision(item) {
				this.$emit('viewInitialDecision', item);
			},
			/**
			 * Emits the selected factor to configuration.vue to display the factor details
			 * @param {Object} factor - evaluation factor object
			 */
			viewFactorDecisions(factor) {
				this.$emit("viewFactor", factor);
			},
			/**
			 * Enable/Disable initial decision
			 * @param {Object} decision - initial decision object
			 */
			onDisableEnableInitialDecision(decision) {
				decision.tenantId = this.activeTenantId;
				decision.isActive = !decision.isActive;

				this.$store.dispatch("updateInitialDecisionMetaData", decision);
			},
			/**
			 * Enable/Disable evaluation factor. Don't send the levels or orderingType to the API
			 * @param {Object} factor - evaluation factor object
			 */
			onDisableEnableFactor(factor) {
				factor.tenantId = this.activeTenantId;
				factor.isActive = !factor.isActive;

				// create a new payload with everything except the levels and orderingType
				let thresholds = [];
				factor.thresholds.map(threshold => {
					let tempThreshold = {
						label: threshold.label,
						placeholder: threshold.placeholder,
						thresholdId: threshold.thresholdId,
						validateRegex: threshold.validateRegex
					}

					thresholds.push(tempThreshold);
				});

				let updateFactor = {
					tenantId: factor.tenantId,
					category: factor.category,
					description: factor.description,
					factorId: factor.factorId,
					functionName: factor.functionName,
					isActive: factor.isActive,
					knowledgeBaseUrl: factor.knowledgeBaseUrl,
					label: factor.label,
					scope: factor.scope,
					thresholds: thresholds,
					treeMode: factor.treeMode,
					type: factor.type,
					whyItWorks: factor.whyItWorks,
				};


				this.$store.dispatch("updateFactorMetaData", updateFactor);
			}
		}

	}
</script>
<!-- styles defined in design/components/_factors-and-decisions.scss -->